import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { installPersistHook, syncRemoteIntoLocal } from "@/lib/persistence";
import { initOfflineQueue } from "@/lib/offlineQueue";
import { applyCanonicalTopology, applyInverterTopology, verifyTopologyIntegrity } from "@/lib/topology";
import { installNotify, registerNavigate, useGrid, VIEWS, type ViewId } from "@/state/grid";
import Chrome from "@/components/Chrome";
import RolePicker from "@/components/RolePicker";
import OverviewView from "@/views/OverviewView";
import PunchView from "@/views/PunchView";
import QcView from "@/views/QcView";
import RfiView from "@/views/RfiView";
import SiteMapView from "@/views/SiteMapView";
import TddView from "@/views/TddView";

const VIEW_COMPONENTS: Record<ViewId, () => JSX.Element> = {
  overview: OverviewView,
  rfi: RfiView,
  sitemap: SiteMapView,
  qc: QcView,
  punch: PunchView,
  tdd: TddView,
};

/**
 * Component-mode shell with React Router. The engine runs HEADLESS (data +
 * domain logic only); React owns the view layer; the URL owns navigation
 * (/overview, /rfi, /sitemap, /qc, /punch, /tdd).
 */
export default function App() {
  const [ready, setReady] = useState(false);
  const loggedIn = useGrid((s) => s.loggedIn);
  const syncView = useGrid((s) => s.syncView);
  const navigate = useNavigate();
  const location = useLocation();

  // Let store actions drive the router.
  useEffect(() => registerNavigate(navigate), [navigate]);

  // Boot the headless engine once.
  useEffect(() => {
    installNotify();
    installPersistHook();
    initOfflineQueue();
    (async () => {
      await syncRemoteIntoLocal();
      window.__gpBootHeadless?.();
      // Swap the demo seed for the real CAD topology (topo_* in Supabase) so QC
      // inspects the exact 1,088 tables the CAD defines. Falls back to the demo
      // seed if the load fails (offline / no Supabase).
      await applyCanonicalTopology();
      // Make QC Electrical's station/inverter numbering match the Site Map (real
      // CAD "TR{n}-INV{m}" designators + true per-station inverter counts). Falls
      // back to the synthetic 8×20 model if no CAD labels are loaded.
      await applyInverterTopology();
      // Issue #11: confirm the engine's per-table layout (what QC inspects)
      // matches the map topology 1:1 for this project; logs a warning on drift.
      verifyTopologyIntegrity().catch(() => {});
      setReady(true);
    })();
  }, []);

  // Keep the engine + store view in sync with the URL (handles deep links,
  // back/forward, and role gating).
  useEffect(() => {
    if (!ready || !loggedIn) return;
    const v = location.pathname.replace(/^\//, "") as ViewId;
    syncView(VIEWS.includes(v) ? v : "overview");
  }, [ready, loggedIn, location.pathname, syncView]);

  if (!ready) return null;
  if (!loggedIn) return <RolePicker />;

  return (
    <div id="app">
      <Chrome />
      <div className="viewport">
        <Routes>
          <Route path="/" element={<Navigate to="/overview" replace />} />
          {VIEWS.map((v) => {
            const Comp = VIEW_COMPONENTS[v];
            return (
              <Route
                key={v}
                path={`/${v}`}
                element={
                  <div className="view active" id={`v-${v}`}>
                    <Comp />
                  </div>
                }
              />
            );
          })}
          <Route path="*" element={<Navigate to="/overview" replace />} />
        </Routes>
      </div>
      {/* Photo lightbox infra for the QC / Punch views. */}
      <div className="lightbox" id="lightbox" style={{ display: "none" }}>
        <button className="lb-close" id="lbclose" aria-label="Close">
          ✕
        </button>
        <button className="lb-nav lb-prev" id="lbprev" aria-label="Previous">
          ‹
        </button>
        <button className="lb-nav lb-next" id="lbnext" aria-label="Next">
          ›
        </button>
        <img id="lbimg" src="" alt="evidence" />
        <div className="lb-cap" id="lbcap" />
        <div className="lb-counter" id="lbcounter" />
      </div>
    </div>
  );
}
